import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Loader2, CheckCircle2, XCircle, Clock } from 'lucide-react';

export type VideoJobStatus = 'queued' | 'in_progress' | 'completed' | 'failed';

interface VideoJobStatusProps {
  status: VideoJobStatus;
  progress?: number; // 0-100
  errorMessage?: string | null;
  showProgress?: boolean;
}

/**
 * VideoJobStatus - Display job status with badge and optional progress
 *
 * Statuses:
 * - queued: Job waiting to start
 * - in_progress: Currently generating
 * - completed: Successfully finished
 * - failed: Generation failed
 */
export const VideoJobStatus: React.FC<VideoJobStatusProps> = ({
  status,
  progress,
  errorMessage,
  showProgress = true
}) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'queued':
        return {
          variant: 'secondary' as const,
          icon: <Clock className="h-3 w-3" />,
          label: 'Queued',
          color: 'text-muted-foreground'
        };
      case 'in_progress':
        return {
          variant: 'default' as const,
          icon: <Loader2 className="h-3 w-3 animate-spin" />,
          label: 'Generating...',
          color: 'text-primary'
        };
      case 'completed':
        return {
          variant: 'default' as const,
          icon: <CheckCircle2 className="h-3 w-3" />,
          label: 'Completed',
          color: 'text-green-600 bg-green-100'
        };
      case 'failed':
        return {
          variant: 'destructive' as const,
          icon: <XCircle className="h-3 w-3" />,
          label: 'Failed',
          color: 'text-destructive'
        };
      default:
        return {
          variant: 'secondary' as const,
          icon: null,
          label: 'Unknown',
          color: ''
        };
    }
  };

  const statusConfig = getStatusConfig();

  return (
    <div className="space-y-2">
      <Badge
        variant={statusConfig.variant}
        className={`flex items-center gap-1 w-fit ${statusConfig.color}`}
      >
        {statusConfig.icon}
        <span>{statusConfig.label}</span>
      </Badge>

      {/* Progress Bar for in_progress status */}
      {showProgress && status === 'in_progress' && (
        <div className="space-y-1">
          <Progress value={progress || 0} className="h-2" />
          {progress !== undefined && (
            <p className="text-xs text-muted-foreground">{Math.round(progress)}% complete</p>
          )}
        </div>
      )}

      {/* Error Message for failed status */}
      {status === 'failed' && errorMessage && (
        <p className="text-xs text-destructive">{errorMessage}</p>
      )}
    </div>
  );
};

export default VideoJobStatus;
